import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Avatar,
  Badge,
  Flex,
  Divider,
  IconButton,
  Textarea,
  useToast,
  Tooltip,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FaPaperPlane, FaImage, FaSmile, FaEllipsisV } from 'react-icons/fa';
import { Message } from '../types';
import { getUserById, mockMessages } from '../data/mockData';

interface MessageInterfaceProps {
  postId: string;
  currentUserId: string;
  otherUserId: string;
  onSendMessage: (content: string) => void;
}

const MessageInterface: React.FC<MessageInterfaceProps> = ({
  postId,
  currentUserId,
  otherUserId,
  onSendMessage
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  const otherUser = getUserById(otherUserId);

  // Load messages for this post
  useEffect(() => {
    const postMessages = mockMessages.filter(msg => msg.postId === postId);
    setMessages(postMessages);
  }, [postId]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = () => {
    if (!newMessage.trim()) return;

    const message: Message = {
      id: Date.now().toString(),
      postId,
      senderId: currentUserId,
      receiverId: otherUserId,
      content: newMessage,
      timestamp: new Date().toISOString(),
      isRead: false
    };

    setMessages(prev => [...prev, message]);
    onSendMessage(newMessage);
    setNewMessage('');

    // Simulate typing indicator and response
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      // Simulate automatic response in demo
      if (Math.random() > 0.5) {
        const autoResponse: Message = {
          id: (Date.now() + 1).toString(),
          postId,
          senderId: otherUserId,
          receiverId: currentUserId,
          content: getAutoResponse(newMessage),
          timestamp: new Date().toISOString(),
          isRead: false
        };
        setMessages(prev => [...prev, autoResponse]);
      }
    }, 1500);

    toast({
      title: 'Message sent',
      status: 'success',
      duration: 2000,
    });
  };

  const getAutoResponse = (_userMessage: string): string => {
    const responses = [
      "Thanks for your interest! The item is still available.",
      "Sure, I can answer any questions you have.",
      "Yes, we can arrange a meetup to check it out.",
      "The condition is exactly as described in the listing.",
      "I'm flexible on the price for serious buyers.",
      "Let me know if you need any additional photos."
    ];
    return responses[Math.floor(Math.random() * responses.length)];
  };

  const formatMessageTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatMessageDate = (timestamp: string) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const groupMessagesByDate = (messages: Message[]) => {
    const groups: { [key: string]: Message[] } = {};
    messages.forEach(message => {
      const date = formatMessageDate(message.timestamp);
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
    });
    return groups;
  };

  const messageGroups = groupMessagesByDate(messages);

  return (
    <VStack spacing={0} h="500px" w="full" bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200">
      {/* Header */}
      <HStack
        w="full"
        p={4}
        bg="gray.50"
        borderTopRadius="lg"
        justify="space-between"
        borderBottom="1px solid"
        borderColor="gray.200"
      >
        <HStack spacing={3}>
          <Avatar
            size="sm"
            name={`${otherUser?.firstName} ${otherUser?.lastName}`}
            src={`https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face`}
          />
          <VStack align="start" spacing={0}>
            <Text fontWeight="semibold">
              {otherUser?.firstName} {otherUser?.lastName}
            </Text>
            <HStack spacing={2}>
              {otherUser?.isVerified && (
                <Badge colorScheme="green" size="sm">Verified</Badge>
              )}
              <Text fontSize="sm" color="gray.500">
                Usually responds in a few hours
              </Text>
            </HStack>
          </VStack>
        </HStack>
        <IconButton
          icon={<FaEllipsisV />}
          aria-label="Message options"
          variant="ghost"
          size="sm"
        />
      </HStack>

      {/* Messages Area */}
      <Box
        flex={1}
        w="full"
        overflowY="auto"
        p={4}
        css={{
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#CBD5E0',
            borderRadius: '24px',
          },
        }}
      >
        {messages.length === 0 ? (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            Start the conversation! Send a message to get in touch about this item.
          </Alert>
        ) : (
          <VStack spacing={4} align="stretch">
            {Object.entries(messageGroups).map(([date, groupMessages]) => (
              <VStack key={date} spacing={2} align="stretch">
                {/* Date Divider */}
                <Flex align="center" my={2}>
                  <Divider />
                  <Text
                    px={3}
                    py={1}
                    bg="gray.100"
                    borderRadius="full"
                    fontSize="sm"
                    color="gray.600"
                    fontWeight="medium"
                    whiteSpace="nowrap"
                  >
                    {date}
                  </Text>
                  <Divider />
                </Flex>

                {/* Messages for this date */}
                {groupMessages.map((message) => {
                  const isCurrentUser = message.senderId === currentUserId;
                  const sender = getUserById(message.senderId);

                  return (
                    <HStack
                      key={message.id}
                      align="end"
                      justify={isCurrentUser ? 'flex-end' : 'flex-start'}
                      spacing={2}
                    >
                      {!isCurrentUser && (
                        <Avatar
                          size="xs"
                          name={`${sender?.firstName} ${sender?.lastName}`}
                        />
                      )}
                      
                      <VStack
                        align={isCurrentUser ? 'end' : 'start'}
                        spacing={1}
                        maxW="70%"
                      >
                        <Box
                          bg={isCurrentUser ? 'brand.primary' : 'gray.100'}
                          color={isCurrentUser ? 'black' : 'gray.800'}
                          px={4}
                          py={2}
                          borderRadius="lg"
                          borderBottomLeftRadius={isCurrentUser ? 'lg' : 'sm'}
                          borderBottomRightRadius={!isCurrentUser ? 'lg' : 'sm'}
                          maxW="full"
                          wordBreak="break-word"
                        >
                          <Text fontSize="sm">{message.content}</Text>
                        </Box>
                        <Text fontSize="xs" color="gray.500">
                          {formatMessageTime(message.timestamp)}
                          {isCurrentUser && !message.isRead && (
                            <Text as="span" ml={1} color="gray.400">
                              ✓
                            </Text>
                          )}
                        </Text>
                      </VStack>

                      {isCurrentUser && (
                        <Avatar
                          size="xs"
                          name={`${sender?.firstName} ${sender?.lastName}`}
                        />
                      )}
                    </HStack>
                  );
                })}
              </VStack>
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <HStack align="end" spacing={2}>
                <Avatar
                  size="xs"
                  name={`${otherUser?.firstName} ${otherUser?.lastName}`}
                />
                <Box
                  bg="gray.100"
                  px={4}
                  py={2}
                  borderRadius="lg"
                  borderBottomLeftRadius="sm"
                >
                  <HStack spacing={1}>
                    <Box w={2} h={2} bg="gray.400" borderRadius="full" animation="pulse 1.5s infinite" />
                    <Box w={2} h={2} bg="gray.400" borderRadius="full" animation="pulse 1.5s infinite 0.2s" />
                    <Box w={2} h={2} bg="gray.400" borderRadius="full" animation="pulse 1.5s infinite 0.4s" />
                  </HStack>
                </Box>
              </HStack>
            )}

            <div ref={messagesEndRef} />
          </VStack>
        )}
      </Box>

      {/* Message Input */}
      <HStack
        w="full"
        p={4}
        borderTop="1px solid"
        borderColor="gray.200"
        borderBottomRadius="lg"
        bg="white"
        spacing={2}
      >
        <IconButton
          icon={<FaImage />}
          aria-label="Add image"
          variant="ghost"
          size="sm"
          color="gray.500"
          _hover={{ color: 'brand.green.medium' }}
        />
        <Textarea
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type a message..."
          resize="none"
          minH="40px"
          maxH="120px"
          borderRadius="full"
          border="1px solid"
          borderColor="gray.300"
          _focus={{ borderColor: 'brand.primary' }}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage();
            }
          }}
        />
        <IconButton
          icon={<FaSmile />}
          aria-label="Add emoji"
          variant="ghost"
          size="sm"
          color="gray.500"
          _hover={{ color: 'brand.green.medium' }}
        />
        <Tooltip label="Send message (Enter)">
          <IconButton
            icon={<FaPaperPlane />}
            aria-label="Send message"
            onClick={handleSendMessage}
            isDisabled={!newMessage.trim()}
            bg="brand.primary"
            color="black"
            _hover={{ bg: 'brand.primaryHover' }}
            _disabled={{
              bg: 'gray.200',
              color: 'gray.400',
              cursor: 'not-allowed'
            }}
          />
        </Tooltip>
      </HStack>
    </VStack>
  );
};

export default MessageInterface; 